<script>
/**
 * Mixin for filter (and sorting) options in gw/checklists.vue
 */

import { uniqBy } from 'lodash';
// Vuex
import { createNamespacedHelpers, mapState } from 'vuex';
import { FETCH_TEAMS } from '@/store/action-types';
import { GET_TEAMS } from '@/store/mutation-types';
import { GET_GW_TEMPLATES } from '@/store/modules/gemba-walks/mutation-types';
import { FETCH_GW_TEMPLATES } from '@/store/modules/gemba-walks/action-types';


const { mapState: gwMapState } = createNamespacedHelpers('gembaWalks');

export default {

  computed: {
    completedGwOptions() {
      // Filters
      return [
        {
          key: 'sortTitle',
          component: 'h2',
          options: {
            class: 'basic-sub-title',
          },
          slots: [{
            component: 'span',
            content: this.$t('message.SORT'),
          }],
        },
        {
          key: 'sortBy',
          component: 'RadioGroup',
          value: this.completedGwOptSortBy,
          options: {
            items: this.completedGwOptSortItems,
            sortable: true,
            class: 'mb10 completedGwOptSortBy',
          },
        },
        {
          key: 'filtersTitle',
          component: 'h2',
          options: {
            class: 'basic-sub-title',
          },
          slots: [{
            component: 'span',
            content: this.$t('message.FILTERS'),
          }],
        },
        {
          key: 'templates',
          component: 'EditableLabelRadio',
          value: this.completedGwOptSelectedTemplates,
          options: {
            items: this.completedGwOptTemplates,
            labelKey: 'name',
            legend: this.$t('message.GW_TEMPLATES_SHORT'),
            multiple: true,
          },
        },
        {
          key: 'gwTitle',
          component: 'div',
          options: {
            class: 'legend legend--bottom-margin generic-field-margin',
          },
          slots: [{
            component: 'span',
            content: this.$t('message.GW_MENU'),
          }],
        },
        {
          key: 'which',
          component: 'RadioGroup',
          value: this.completedGwOptWhich,
          options: {
            items: this.completedGwOptWhichItems,
          },
        },
      ];
    },
    completedGwOptSortItems() {
      return [
        { id: 0, label: this.$t('message.SORT_BY_ENTRY_DATE') },
      ];
    },
    completedGwOptSortBy() {
      if (this.allPageOptions && this.allPageOptions.sortBy) {
        return this.allPageOptions.sortBy;
      }
      return { value: 0, desc: true };
    },
    /**
     * Array of teams that the current team has access to.
     * TODO: Replace with a proper access logic following the hierarchy tree and teamusers accesses
     * Same in in gw/templates
     * @return {Array} array of teams
     */
    completedGwOptAccessibleTeams() {
      const hl = (this.currentTeam && this.currentTeam.hierarchyLevel) || 0;
      return uniqBy(this.$store.getters.allTeamsTreeFlattened, 'id')
        .filter(team => team.hierarchyLevel <= hl);
    },
    completedGwOptTemplates() {
      const { templatesPerTeam } = this.$store.state.gembaWalks;
      return this.completedGwOptAccessibleTeams
        .filter(team => templatesPerTeam[`${team.id}`] && templatesPerTeam[`${team.id}`].length)
        .map(team => ({ id: team.id, title: team.name, radioItems: templatesPerTeam[`${team.id}`] || [] }))
        .sort((team1, team2) => {
          if (team1.id === this.currentTeam.id) {
            return -1;
          }
          return team2.id === this.currentTeam.id ? 1 : 0;
        });
    },
    completedGwOptSelectedTemplates() {
      if (this.allPageOptions && this.allPageOptions.templates) {
        return this.allPageOptions.templates;
      }
      return this.completedGwOptDefaultTemplates;
    },
    completedGwOptDefaultTemplates() {
      const { templatesPerTeam } = this.$store.state.gembaWalks;
      return this.completedGwOptAccessibleTeams
        .flatMap(team => templatesPerTeam[`${team.id}`] || [])
        .map(t => t.id);
    },
    completedGwOptWhichItems() {
      return [
        { id: 0, label: this.$t('message.GW_BY_MY_TEAM') },
        { id: 1, label: this.$t('message.GW_FOR_MY_TEAM') },
      ];
    },
    completedGwOptWhich() {
      if (this.allPageOptions && this.allPageOptions.which) {
        return this.allPageOptions.which;
      }
      return 0;
    },
    /** If defined, contains all the options above */
    allPageOptions() {
      return null;
    },
    ...mapState({
      allTeamsLoading: state => state[GET_TEAMS.loadingKey],
    }),
    ...gwMapState({
      gwTemplatesLoading: state => state[GET_GW_TEMPLATES.loadingKey],
    }),
  },

  created() {
    if (this.currentTeam && this.currentTeam.id && !this.gwTemplatesLoading) {
      // Fetch all templates
      this.$store.dispatch(`gembaWalks/${FETCH_GW_TEMPLATES}`);
    }
    if (this.currentTeam && this.currentTeam.id && !this.allTeamsLoading) {
      // Fetch all teams for computed completedGwOptAccessibleTeams
      this.$store.dispatch(FETCH_TEAMS);
    }
  },

  methods: {
    /** Create a string formatted as query params with params */
    completedGwOptQueryParams(options) {
      if (!options) {
        return '';
      }
      let query = '';
      let amp = '';
      if (options.sortBy) {
        query += `sortBy=${options.sortBy.value}&sortDesc=${options.sortBy.desc}`;
        amp = '&';
      }
      if (options.templates && options.templates.length) {
        query += `${amp}completedGwTemplateIds=${options.templates.join(',')}`;
        amp = '&';
      }
      if (options.which) {
        query += `${amp}whichItemsChoice=${Number(options.which)}`;
        amp = '&';
      }
      return query;
    },
  },
};
</script>
